'use client'

// Ruins — a standalone baked model (position/rotation/scale on the node),
// dropped in at identity. Draco geometry + a KTX2 (Basis ETC1S) base-colour
// texture, now BAKED INTO the GLB so it loads through the single shared
// useModel path (one Draco + one KTX2 loader for the whole scene) — same as
// every other model. The material is rebuilt as a MeshStandardNodeMaterial
// so it lights, shadows, and runs through RendererDither with the world.

import { useEffect } from 'react'
import * as THREE from 'three/webgpu'
import { useModel } from '../hooks/useModel'

const MODEL = '/models/ruins.glb'

export function Ruins() {
  const { scene } = useModel(MODEL)

  useEffect(() => {
    // Convert the glTF material to a fresh MeshStandardNodeMaterial (the
    // useNodeMaterials pattern — set maps/params EXPLICITLY, never copy(),
    // which drops undefined into the node slots and kills the shader). The
    // embedded KTX2 map already carries the right colorSpace/flipY from the
    // glTF loader, so it just transfers.
    const converted = new Map()
    scene.traverse((o) => {
      if (!o.isMesh) return
      o.castShadow = true
      o.receiveShadow = true
      const src = o.material
      if (!src || Array.isArray(src) || src.isNodeMaterial) return
      let nm = converted.get(src)
      if (!nm) {
        nm = new THREE.MeshStandardNodeMaterial()
        if (src.map) nm.map = src.map
        nm.color = src.color?.clone() ?? new THREE.Color('white')
        nm.roughness = src.roughness ?? 0.9
        nm.metalness = src.metalness ?? 0
        nm.side = src.side
        nm.vertexColors = src.vertexColors
        converted.set(src, nm)
      }
      o.material = nm
    })
  }, [scene])

  // Transform is baked into the node — render at identity.
  return <primitive object={scene} />
}

export default Ruins
